import Client from "../client";
import {
  buildODataFilter,
  buildODataQueryString,
  getODataSearchParams,
} from "../odata";
import {
  ControlCode,
  ControlMonitorPoliciesParams,
  ControlMonitorPoliciesResponse,
  ControlMonitorSummaryParams,
  ControlMonitorSummaryResponse,
  CreateObjectExceptionParams,
  CreatePolicyExceptionParams,
  CreateThreatParams,
  CreateViewParams,
  DeletePolicyExceptionParams,
  EndpointsParams,
  GeneratedSCMThreatIntel,
  Group,
  GroupParams,
  InboxesParams,
  Notification,
  ObjectException,
  PartnerEndpointsResponse,
  PartnerInboxesResponse,
  PartnerUsersResponse,
  PolicyException,
  RequestOptions,
  SaveNotificationParams,
  SCMThreat,
  StatusResponse,
  TechniqueSummaryResponse,
  UpdateObjectExceptionParams,
  UpdatePartnerGroupsParams,
  UpdatePolicyExceptionParams,
  UpdateThreatParams,
  UpdateViewParams,
  UsersParams,
  View,
  ViewResponse,
} from "../types";

export default class ScmController {
  #client: Client;

  constructor(client: Client) {
    this.#client = client;
  }

  async users(
    { query }: UsersParams,
    options: RequestOptions = {},
  ): Promise<PartnerUsersResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/users${buildODataQueryString(query)}`,
      {
        method: "GET",
        ...options,
      },
    );

    return response.json();
  }

  async inboxes(
    { query }: InboxesParams,
    options: RequestOptions = {},
  ): Promise<PartnerInboxesResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/inboxes${buildODataQueryString(query)}`,
      {
        method: "GET",
        ...options,
      },
    );

    return response.json();
  }

  /** Get a list of endpoints from all partners */
  async endpoints(
    { query }: EndpointsParams,
    options: RequestOptions = {},
  ): Promise<PartnerEndpointsResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/endpoints${buildODataQueryString(query)}`,
      {
        method: "GET",
        ...options,
      },
    );

    return response.json();
  }

  async objectExceptions(
    options: RequestOptions = {},
  ): Promise<ObjectException[]> {
    const response = await this.#client.requestWithAuth(
      "/scm/exceptions/objects",
      { method: "GET", ...options },
    );
    return response.json();
  }

  async policyExceptions(
    options: RequestOptions = {},
  ): Promise<PolicyException[]> {
    const response = await this.#client.requestWithAuth(
      "/scm/exceptions/policies",
      { method: "GET", ...options },
    );
    return response.json();
  }

  async createObjectException(
    { filter, ...params }: CreateObjectExceptionParams,
    options: RequestOptions = {},
  ): Promise<{ exception_id: string }> {
    const response = await this.#client.requestWithAuth(
      "/scm/exceptions/objects",
      {
        method: "POST",
        body: JSON.stringify({
          ...params,
          filter: buildODataFilter({ filter }),
        }),
        ...options,
      },
    );
    return response.json();
  }

  async updateObjectException(
    { id, filter, ...params }: UpdateObjectExceptionParams,
    options: RequestOptions = {},
  ): Promise<{ exception_id: string }> {
    const response = await this.#client.requestWithAuth(
      `/scm/exceptions/objects/${id}`,
      {
        method: "POST",
        body: JSON.stringify({
          ...params,
          filter: buildODataFilter({ filter }),
        }),
        ...options,
      },
    );
    return response.json();
  }

  async deleteObjectException(
    id: string,
    options: RequestOptions = {},
  ): Promise<void> {
    const response = await this.#client.requestWithAuth(
      `/scm/exceptions/objects/${id}`,
      {
        method: "DELETE",
        ...options,
      },
    );
    return response.json();
  }

  async createPolicyException(
    {
      instanceId,
      control,
      policyId,
      settingNames,
      expires,
    }: CreatePolicyExceptionParams,
    options: RequestOptions = {},
  ): Promise<{ status: boolean }> {
    const response = await this.#client.requestWithAuth(
      `/scm/exceptions/policies`,
      {
        method: "POST",
        body: JSON.stringify({
          control,
          instance_id: instanceId,
          policy_id: policyId,
          setting_names: settingNames,
          expires: expires && expires.toISOString(),
        }),
        ...options,
      },
    );
    return response.json();
  }

  async updatePolicyException(
    {
      policyId,
      instanceId,
      settingNames,
      expires,
      control,
    }: UpdatePolicyExceptionParams,
    options: RequestOptions = {},
  ): Promise<{ status: boolean }> {
    const response = await this.#client.requestWithAuth(
      `/scm/exceptions/policies`,
      {
        method: "PUT",
        body: JSON.stringify({
          control,
          instance_id: instanceId,
          policy_id: policyId,
          setting_names: settingNames,
          expires: expires && expires.toISOString(),
        }),
        ...options,
      },
    );
    return response.json();
  }

  async deletePolicyException(
    { instanceId, policyId, control }: DeletePolicyExceptionParams,
    options: RequestOptions = {},
  ): Promise<{ status: boolean }> {
    const response = await this.#client.requestWithAuth(
      `/scm/exceptions/policies`,
      {
        method: "DELETE",
        body: JSON.stringify({
          instance_id: instanceId,
          policy_id: policyId,
          control,
        }),
        ...options,
      },
    );
    return response.json();
  }

  async getControlMonitorSummary(
    {
      endpointsQuery,
      usersQuery,
      inboxesQuery,
      techniques,
    }: ControlMonitorSummaryParams,
    options: RequestOptions = {},
  ): Promise<ControlMonitorSummaryResponse> {
    const searchParams = new URLSearchParams();

    if (endpointsQuery)
      searchParams.set("endpoints_filter", buildODataFilter(endpointsQuery));
    if (usersQuery)
      searchParams.set("users_filter", buildODataFilter(usersQuery));
    if (inboxesQuery)
      searchParams.set("inboxes_filter", buildODataFilter(inboxesQuery));

    if (techniques && techniques.length > 0)
      searchParams.set("techniques", techniques.join(","));

    const response = await this.#client.requestWithAuth(
      `/scm/evaluation_summary?${searchParams.toString()}`,
      { method: "GET", ...options },
    );

    return await response.json();
  }

  async getTechniqueSummaries(
    techniques: string[],
    options: RequestOptions = {},
  ): Promise<TechniqueSummaryResponse[]> {
    const searchParams = new URLSearchParams();
    if (techniques && techniques.length > 0)
      searchParams.set("techniques", techniques.join(","));

    const response = await this.#client.requestWithAuth(
      `/scm/technique_summary?${searchParams.toString()}`,
      { method: "GET", ...options },
    );

    return await response.json();
  }

  async listPolicies(
    control: ControlCode,
    instanceId: string,
    params?: ControlMonitorPoliciesParams,
    options: RequestOptions = {},
  ): Promise<ControlMonitorPoliciesResponse> {
    const searchParams = getODataSearchParams(params?.query);
    if (params?.techniques && params.techniques.length > 0)
      searchParams.set("techniques", params.techniques.join(","));

    const response = await this.#client.requestWithAuth(
      `/scm/evaluations/${control}/${instanceId}?${searchParams.toString()}`,
      { method: "GET", ...options },
    );

    return await response.json();
  }

  async updatePolicy(
    control: ControlCode,
    instanceId: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/evaluations/${control}/${instanceId}`,
      {
        method: "POST",
        ...options,
      },
    );

    return await response.json();
  }

  async listNotifications(
    options: RequestOptions = {},
  ): Promise<Notification[]> {
    const response = await this.#client.requestWithAuth("/scm/notifications", {
      method: "GET",
      ...options,
    });

    return await response.json();
  }

  async deleteNotification(
    id: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/notifications/${id}`,
      { method: "DELETE", ...options },
    );

    return await response.json();
  }

  async saveNotification(
    { filter, ...body }: SaveNotificationParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth("/scm/notifications", {
      method: "PUT",
      body: JSON.stringify({
        filter: filter && buildODataFilter({ filter }),
        ...body,
      }),
      ...options,
    });

    return await response.json();
  }

  async listThreats(options: RequestOptions = {}): Promise<SCMThreat[]> {
    const response = await this.#client.requestWithAuth("/scm/threats", {
      method: "GET",
      ...options,
    });

    const threats = await response.json();

    return threats;
  }

  async createThreat(
    params: CreateThreatParams,
    options: RequestOptions = {},
  ): Promise<SCMThreat> {
    const response = await this.#client.requestWithAuth("/scm/threats", {
      method: "POST",
      body: JSON.stringify(params),
      ...options,
    });

    return await response.json();
  }

  async updateThreat(
    id: string,
    params: UpdateThreatParams,
    options: RequestOptions = {},
  ): Promise<SCMThreat> {
    const response = await this.#client.requestWithAuth(`/scm/threats/${id}`, {
      method: "POST",
      body: JSON.stringify(params),
      ...options,
    });
    return await response.json();
  }

  async deleteThreat(
    id: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(`/scm/threats/${id}`, {
      method: "DELETE",
      ...options,
    });
    return await response.json();
  }

  async getThreat(
    id: string,
    options: RequestOptions = {},
  ): Promise<SCMThreat | undefined> {
    const response = await this.#client.requestWithAuth(`/scm/threats/${id}`, {
      method: "GET",
      ...options,
    });

    return (await response.json()) as SCMThreat;
  }

  async generateThreatIntel(
    file: File,
    options: RequestOptions = {},
  ): Promise<GeneratedSCMThreatIntel> {
    const headers = {
      "Content-Type": "application/pdf",
      ...options.headers,
    };

    const response = await this.#client.requestWithAuth("/scm/threat-intel", {
      method: "POST",
      body: await file.arrayBuffer(),
      headers,
      ...options,
    });

    return await response.json();
  }

  async listViews(options: RequestOptions = {}): Promise<View[]> {
    const response = await this.#client.requestWithAuth("/scm/views", {
      method: "GET",
      ...options,
    });

    return await response.json();
  }

  async createView(
    params: CreateViewParams,
    options: RequestOptions = {},
  ): Promise<ViewResponse> {
    const response = await this.#client.requestWithAuth("/scm/views", {
      method: "POST",
      body: JSON.stringify(params),
      ...options,
    });

    return await response.json();
  }

  async updateView(
    { viewId, ...params }: UpdateViewParams,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/views/${viewId}`,
      {
        method: "POST",
        body: JSON.stringify(params),
        ...options,
      },
    );

    return await response.json();
  }

  async deleteView(
    viewId: string,
    options: RequestOptions = {},
  ): Promise<StatusResponse> {
    const response = await this.#client.requestWithAuth(
      `/scm/views/${viewId}`,
      {
        method: "DELETE",
        ...options,
      },
    );

    return await response.json();
  }

  async groups(
    { query }: GroupParams = {},
    options: RequestOptions = {},
  ): Promise<Group[]> {
    const response = await this.#client.requestWithAuth(
      `/scm/groups${buildODataQueryString(query)}`,
      {
        method: "GET",
        ...options,
      },
    );

    return response.json();
  }

  async updatePartnerGroups(
    params: UpdatePartnerGroupsParams,
    options: RequestOptions = {},
  ): Promise<{
    job_id: string;
  }> {
    const { control, instanceId, groupIds } = params;
    const response = await this.#client.requestWithAuth(
      `/scm/groups/${control}/${instanceId}`,
      {
        method: "POST",
        body: JSON.stringify({ group_ids: groupIds }),
        ...options,
      },
    );

    return response.json();
  }
}
